import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js'
import UserPool from './config'

import {
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_NEW_PASSWORD_REQUIRED,
  REFRESH_SESSION_SUCCESS,
  REFRESH_SESSION_FAILURE,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAILURE,
  CHANGE_PASSWORD_INCORRECT_PASSWORD,
} from './statuses'

// Stateful utilities
export const getUserFromPool = async () =>
  new Promise((resolve, reject) => {
    const user = UserPool.getCurrentUser()
    if (user) {
      resolve(user)
    } else {
      reject('User not logged in')
    }
  })

// Stateless utilities
/**
 * This uses getSession under the hood, which seems to **mutate** poolUser,
 * appending the session to it.
 * @param {Object} poolUser
 * @returns {Promise} A promise that, when resolved, returns a session object
 */
export const loadSessionIntoPoolUser = async poolUser =>
  new Promise((resolve, reject) => {
    poolUser.getSession((error, session) => {
      if (error) {
        console.error('[Error getting user session]:', error)
        reject(error)
      } else {
        resolve(session)
      }
    })
  })

export const getUserAttributeFromSession = (session, attribute) => {
  const sessionPayload = session.idToken.payload

  return sessionPayload[attribute] ?? null
}

// Stateless use-cases
export const authenticateUser = async (username, password) =>
  new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: username,
      Pool: UserPool,
    })

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    user.authenticateUser(authDetails, {
      onSuccess: session => {
        resolve({
          status: LOGIN_SUCCESS,
          data: {
            session,
          },
        })
      },
      onFailure: error => {
        reject({
          status: LOGIN_FAILURE,
          error,
        })
      },
      // NOTE "User was signed up by an admin and must provide new password
      // and required attributes, if any, to complete authentication."
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        reject({
          status: LOGIN_NEW_PASSWORD_REQUIRED,
          data: {
            userAttributes,
            user,
          },
        })
      },
    })
  })

export const refreshSession = async (cognitoUser, refreshToken) =>
  new Promise((resolve, reject) => {
    cognitoUser.refreshSession(refreshToken, (error, newSession) => {
      if (error) {
        console.error(error.message)
        reject({
          status: REFRESH_SESSION_FAILURE,
          error,
        })
      } else {
        resolve({
          status: REFRESH_SESSION_SUCCESS,
          data: { newSession },
        })
      }
    })
  })

export const changePassword = async (cognitoUser, oldPassword, newPassword) =>
  // IMPORTANT This session loading is required for some reason to avoid an
  // "User is not authenticated" error.
  // Source 1: https://github.com/amazon-archives/amazon-cognito-identity-js/issues/35
  // Source 2: https://github.com/amazon-archives/amazon-cognito-identity-js/issues/71
  loadSessionIntoPoolUser(cognitoUser).then(() => {
    return new Promise((resolve, reject) => {
      cognitoUser.changePassword(oldPassword, newPassword, (error, result) => {
        if (error) {
          console.error(error.code, error.message)

          const status =
            error.code === 'NotAuthorizedException'
              ? CHANGE_PASSWORD_INCORRECT_PASSWORD
              : CHANGE_PASSWORD_FAILURE
          reject({
            status,
            error,
          })
        } else {
          resolve({
            status: CHANGE_PASSWORD_SUCCESS,
            data: { result },
          })
        }
      })
    })
  })

export const signOut = async cognitoUser =>
  new Promise((resolve, reject) => {
    cognitoUser.signOut(() => {
      resolve()
    })
  })
